import React, { useState, useEffect } from 'react';
// import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon } from 'lucide-react';
import { getAboutPageData, updateAboutPageData, uploadFileToServer } from '../services/api';

const MODULES = {
  toolbar: [
    [{ 'header': [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ],
};

const ManageAboutUs = () => {
  const [data, setData] = useState({
    pageTitle: '',
    metaTitle: '',
    metaDescription: '',
    heroHeading: '',
    heroSubheading: '',
    heroImage: '',
    mainContent: '',
    missionTitle: '',
    missionDesc: '',
    visionTitle: '',
    visionDesc: '',
    optionalSections: [],
    teamMembers: [],
    ctaText: '',
    ctaBtnLabel: '',
    ctaBtnLink: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const cleanHTML = (html) => {
    return (html || "")
      .replace(/<p>(&nbsp;|\s)*<\/p>/g, "")  // remove empty <p>
      .replace(/&nbsp;/g, " ")               // replace nbsp
      .trim();
  };
  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getAboutPageData();
      const validResult = result || {};
      
      setData(prev => ({
        ...prev,
        ...validResult,
        pageTitle: validResult.pageTitle || '',
        metaTitle: validResult.metaTitle || '',
        metaDescription: validResult.metaDescription || '',
        heroHeading: validResult.heroHeading || '',
        heroSubheading: validResult.heroSubheading || '',
        heroImage: validResult.heroImage || '',
        mainContent: validResult.mainContent || '',
        missionTitle: validResult.missionTitle || '',
        missionDesc: validResult.missionDesc || '',
        visionTitle: validResult.visionTitle || '',
        visionDesc: validResult.visionDesc || '',
        ctaText: validResult.ctaText || '',
        ctaBtnLabel: validResult.ctaBtnLabel || '',
        ctaBtnLink: validResult.ctaBtnLink || '',
        optionalSections: Array.isArray(validResult.optionalSections) ? validResult.optionalSections : [],
        teamMembers: Array.isArray(validResult.teamMembers) ? validResult.teamMembers : []
      }));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: 'info', text: 'Saving...' });

    const cleanData = {
      ...data,
      mainContent: cleanHTML(data.mainContent || ''),
      missionDesc: cleanHTML(data.missionDesc || ''),
      visionDesc: cleanHTML(data.visionDesc || ''),
      optionalSections: (data.optionalSections || [])
        .filter(s => (s.title || '').trim() !== '' || (s.content || '').trim() !== '')
        .map(s => ({ ...s, content: cleanHTML(s.content || '') })),
      teamMembers: (data.teamMembers || []).filter(m => (m.name || '').trim() !== '')
    };

    const res = await updateAboutPageData(cleanData);
    if (res.success) {
      setMessage({ type: 'success', text: 'About Us page updated successfully!' });
      setData(cleanData);
    } else {
      setMessage({ type: 'error', text: 'Failed to update. Please try again.' });
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 3000);
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setMessage({ type: 'info', text: 'Uploading image...' });
    const res = await uploadFileToServer(file);
    if (res.success) {
      setData({ ...data, [field]: res.url });
      setMessage({ type: 'success', text: 'Image uploaded!' });
    } else {
      setMessage({ type: 'error', text: 'Upload failed.' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Optional Sections Functions
  const addSection = () => {
    setData({
      ...data,
      optionalSections: [...data.optionalSections, { title: '', content: '' }]
    });
  };

  const updateSection = (index, field, value) => {
    const updated = [...data.optionalSections];
    updated[index][field] = value;
    setData({ ...data, optionalSections: updated });
  };

  const removeSection = (index) => {
    const updated = data.optionalSections.filter((_, i) => i !== index);
    setData({ ...data, optionalSections: updated });
  };

  const moveSection = (index, direction) => {
    if (direction === -1 && index === 0) return;
    if (direction === 1 && index === data.optionalSections.length - 1) return;

    const updated = [...data.optionalSections];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    setData({ ...data, optionalSections: updated });
  };

  // Team Member Functions
  const addTeamMember = () => {
    setData({
      ...data,
      teamMembers: [...data.teamMembers, { name: '', role: '', photo: '', bio: '' }]
    });
  };

  const updateTeamMember = (index, field, value) => {
    const updated = [...data.teamMembers];
    updated[index][field] = value;
    setData({ ...data, teamMembers: updated });
  };

  const removeTeamMember = (index) => {
    const updated = data.teamMembers.filter((_, i) => i !== index);
    setData({ ...data, teamMembers: updated });
  };

  if (loading) return <div className="p-8 text-center text-stone-500">Loading editor...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-stone-900">Manage About Us Page</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center bg-saffron-600 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-saffron-700 transition-colors disabled:bg-stone-300"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Public Page'}
        </button>
      </div>

      {message && (
        <div className={`p-4 mb-6 rounded-xl text-sm font-medium ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
          message.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
            'bg-blue-100 text-blue-800 border border-blue-200'
          }`}>
          {message.text}
        </div>
      )}

      <div className="space-y-8">

        {/* SEO & Meta Section */}
        <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
          <h2 className="text-lg font-bold text-stone-800 mb-4 border-b pb-2">1. SEO & Core Identifiers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Display Page Title (H1)</label>
              <input type="text" value={data.pageTitle} onChange={e => setData({ ...data, pageTitle: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" placeholder="E.g. About Us" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Meta Title (Browser Tab)</label>
              <input type="text" value={data.metaTitle} onChange={e => setData({ ...data, metaTitle: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-2">Meta Description</label>
              <textarea value={data.metaDescription} onChange={e => setData({ ...data, metaDescription: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500 resize-none h-20" />
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
          <h2 className="text-lg font-bold text-stone-800 mb-4 border-b pb-2">2. Hero Banner</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Hero Heading</label>
              <input type="text" value={data.heroHeading} onChange={e => setData({ ...data, heroHeading: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Hero Subheading</label>
              <input type="text" value={data.heroSubheading} onChange={e => setData({ ...data, heroSubheading: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-2">Hero Background Image</label>
              <div className="flex gap-4 items-center">
                <input type="text" value={data.heroImage} onChange={e => setData({ ...data, heroImage: e.target.value })} className="flex-1 border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" placeholder="Image URL..." />
                <span className="text-sm font-bold text-stone-400">OR</span>
                <label className="flex items-center cursor-pointer bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-2.5 rounded-lg border border-stone-300 transition-colors">
                  <ImageIcon className="w-5 h-5 mr-2" /> Upload
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'heroImage')} />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Core Main Content */}
        <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
          <h2 className="text-lg font-bold text-stone-800 mb-4 border-b pb-2">3. Primary Content & Objectives</h2>

          <div className="mb-8">
            <label className="block text-sm font-medium text-stone-700 mb-2">Main Body Text (History, Overview)</label>
            <div className="bg-white rounded-lg border border-stone-300 overflow-hidden">
              <textarea value={data.mainContent} onChange={(e) => setData({ ...data, mainContent: e.target.value })} className="w-full h-64 p-2 mb-10 border border-stone-300 rounded" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Mission Title</label>
              <input type="text" value={data.missionTitle} onChange={e => setData({ ...data, missionTitle: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500 mb-4" placeholder="E.g. Our Mission" />
              <label className="block text-sm font-medium text-stone-700 mb-2">Mission Description</label>
              <textarea value={data.missionDesc} onChange={(e) => setData({ ...data, missionDesc: e.target.value })} className="w-full h-40 p-2 mb-10 border border-stone-300 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Vision Title</label>
              <input type="text" value={data.visionTitle} onChange={e => setData({ ...data, visionTitle: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500 mb-4" placeholder="E.g. Our Vision" />
              <label className="block text-sm font-medium text-stone-700 mb-2">Vision Description</label>
              <textarea value={data.visionDesc} onChange={(e) => setData({ ...data, visionDesc: e.target.value })} className="w-full h-40 p-2 mb-10 border border-stone-300 rounded" />
            </div>
          </div>
        </div>

        {/* Extra Sections */}
        <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
          <div className="flex justify-between items-center mb-4 border-b pb-2">
            <h2 className="text-lg font-bold text-stone-800">4. Extra Content Blocks</h2>
            <button onClick={addSection} className="flex items-center text-sm font-semibold text-saffron-600 hover:text-saffron-800 transition">
              <Plus className="w-4 h-4 mr-1" /> Add Block
            </button>
          </div>

          {data.optionalSections.map((sec, i) => (
            <div key={i} className="mb-6 bg-stone-50 p-4 rounded-xl border border-stone-200 relative">
              <div className="absolute top-4 right-4 flex space-x-2">
                <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1.5 text-stone-500 hover:text-stone-800 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => moveSection(i, 1)} disabled={i === data.optionalSections.length - 1} className="p-1.5 text-stone-500 hover:text-stone-800 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => removeSection(i)} className="p-1.5 text-red-500 hover:text-red-700 ml-4"><Trash2 className="w-4 h-4" /></button>
              </div>
              <h3 className="font-semibold mb-4 text-stone-700">Block #{i + 1}</h3>
              <input type="text" value={sec.title} onChange={e => updateSection(i, 'title', e.target.value)} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500 mb-4 bg-white" placeholder="Block Title" />
              <div className="bg-white rounded-lg border border-stone-300 overflow-hidden">
                <textarea value={sec.content} onChange={(e) => updateSection(i, 'content', e.target.value)} className="w-full h-40 p-2 mb-10 border border-stone-300 rounded" />
              </div>
            </div>
          ))}
          {data.optionalSections.length === 0 && <p className="text-sm text-stone-500 italic py-2">No extra dynamic blocks added.</p>}
        </div>

        {/* Team Members */}
        <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
          <div className="flex justify-between items-center mb-4 border-b pb-2">
            <h2 className="text-lg font-bold text-stone-800">5. Team / Administration</h2>
            <button onClick={addTeamMember} className="flex items-center text-sm font-semibold text-saffron-600 hover:text-saffron-800 transition">
              <Plus className="w-4 h-4 mr-1" /> Add Member
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {data.teamMembers.map((member, i) => (
              <div key={i} className="bg-stone-50 p-4 rounded-xl border border-stone-200 relative">
                <button onClick={() => removeTeamMember(i)} className="absolute top-4 right-4 p-1.5 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                <div className="grid grid-cols-2 gap-4 mb-4 pr-8">
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Name</label>
                    <input type="text" value={member.name} onChange={e => updateTeamMember(i, 'name', e.target.value)} className="w-full border border-stone-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-saffron-500 bg-white text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1">Role/Designation</label>
                    <input type="text" value={member.role} onChange={e => updateTeamMember(i, 'role', e.target.value)} className="w-full border border-stone-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-saffron-500 bg-white text-sm" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-stone-700 mb-1">Photo URL</label>
                    <input type="text" value={member.photo} onChange={e => updateTeamMember(i, 'photo', e.target.value)} className="w-full border border-stone-300 rounded-lg p-2 outline-none focus:ring-2 focus:ring-saffron-500 bg-white text-sm" placeholder="https://..." />
                  </div>
                </div>
                <label className="block text-xs font-medium text-stone-700 mb-1">Short Bio</label>
                <div className="bg-white rounded-lg border border-stone-300 overflow-hidden">
                  <textarea value={member.bio} onChange={(e) => updateTeamMember(i, 'bio', e.target.value)} className="w-full h-32 p-2 mb-10 border border-stone-300 rounded" />
                </div>
              </div>
            ))}
          </div>
          {data.teamMembers.length === 0 && <p className="text-sm text-stone-500 italic py-2">No team members added.</p>}
        </div>

        {/* Call to Action */}
        <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
          <h2 className="text-lg font-bold text-stone-800 mb-4 border-b pb-2">6. Page Footer CTA</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-stone-700 mb-2">Call To Action Text</label>
              <input type="text" value={data.ctaText} onChange={e => setData({ ...data, ctaText: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" placeholder="E.g. Want to sponsor a temple?" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Button Label</label>
              <input type="text" value={data.ctaBtnLabel} onChange={e => setData({ ...data, ctaBtnLabel: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" placeholder="E.g. Contact Us" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-2">Button Target Link</label>
              <input type="text" value={data.ctaBtnLink} onChange={e => setData({ ...data, ctaBtnLink: e.target.value })} className="w-full border border-stone-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-saffron-500" placeholder="E.g. /contact-us or https://external.com" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ManageAboutUs;
